import React, {useState} from 'react'
import {Box, Button, Chip, TextField, Typography} from '@mui/material'
import type {DiscoveryQuestionData, SiteBuilderOption} from './types'

interface DiscoveryQuestionWidgetProps {
  data: DiscoveryQuestionData
  onSubmit: (formattedMessage: string) => void
}

const DiscoveryQuestionWidget: React.FC<DiscoveryQuestionWidgetProps> = ({data, onSubmit}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [textValue, setTextValue] = useState('')
  const [numberValue, setNumberValue] = useState('')

  const handleOptionClick = (option: SiteBuilderOption) => {
    if (data.type === 'single_choice') {
      setSelected(new Set([option.id]))
    } else if (data.type === 'multiple_choice') {
      setSelected(prev => {
        const next = new Set(prev)
        if (next.has(option.id)) next.delete(option.id)
        else next.add(option.id)
        return next
      })
    }
  }

  const handleSubmit = () => {
    if (data.type === 'open_text') {
      if (!textValue.trim()) return
      onSubmit(textValue.trim())
      return
    }
    if (data.type === 'number') {
      if (!numberValue.trim()) return
      onSubmit(numberValue.trim())
      return
    }
    if (selected.size === 0) return
    const selectedOptions = (data.options ?? []).filter(o => selected.has(o.id))
    onSubmit(selectedOptions.map(o => o.label).join(', '))
  }

  const canSubmit =
    data.type === 'open_text' ? textValue.trim().length > 0 :
    data.type === 'number' ? numberValue.trim().length > 0 :
    selected.size > 0

  return (
    <Box
      sx={{
        mx: 2,
        mb: 1,
        p: 2,
        border: 1,
        borderColor: 'primary.light',
        borderRadius: 2,
        bgcolor: 'background.paper',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}
    >
      {data.suggestion && (
        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 1, fontStyle: 'italic'}}>
          {data.suggestion}
        </Typography>
      )}

      <Typography variant="body2" fontWeight={600} sx={{mb: 1.5}}>
        {data.question}
      </Typography>

      {data.type === 'open_text' && (
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          maxRows={5}
          value={textValue}
          onChange={e => setTextValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleSubmit()
            }
          }}
          placeholder="Type your answer..."
          sx={{mb: 1.5}}
        />
      )}

      {data.type === 'number' && (
        <TextField
          fullWidth
          size="small"
          type="number"
          value={numberValue}
          onChange={e => setNumberValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleSubmit()
            }
          }}
          placeholder="Enter a number..."
          sx={{mb: 1.5}}
        />
      )}

      {(data.type === 'single_choice' || data.type === 'multiple_choice') && (
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5}}>
          {(data.options ?? []).map(option => (
            <Chip
              key={option.id}
              label={option.label}
              onClick={() => handleOptionClick(option)}
              variant={selected.has(option.id) ? 'filled' : 'outlined'}
              color={selected.has(option.id) ? 'primary' : 'default'}
              sx={{
                cursor: 'pointer',
                ...(option.description ? {
                  height: 'auto',
                  '& .MuiChip-label': {
                    display: 'block',
                    whiteSpace: 'normal',
                    py: 0.75
                  }
                } : {})
              }}
            />
          ))}
        </Box>
      )}

      {/* Show description for selected option */}
      {(data.type === 'single_choice' || data.type === 'multiple_choice') && selected.size > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 1}}>
          {(data.options ?? []).find(o => selected.has(o.id))?.description}
        </Typography>
      )}

      <Button
        variant="contained"
        size="small"
        onClick={handleSubmit}
        disabled={!canSubmit}
        sx={{textTransform: 'none'}}
      >
        Submit
      </Button>
    </Box>
  )
}

export default DiscoveryQuestionWidget
